import React, {useRef, useState } from 'react';
import {useGetDashboard, useUpdateDashboard} from "api/api-hooks/apiHooks";
import {Button} from "primereact/button";
import {Toolbar} from "primereact/toolbar";
import {Dialog} from "primereact/dialog";
import {Toast} from "primereact/toast";
import ErrorBoundary from "../../ErrorBoundary";
import {Dashboard} from "api/model/dashboard/Dashboard";
import {DashboardComponentMappingWrapper} from "../dashboard/components/dashboard-utils";
import {DashboardReportsApi} from "api/DashboardReportsApi";
import {DashboardReportEditor} from "./DashboardReportEditor";
import { Calendar } from 'primereact/calendar';
import {addToDate, truncateDate} from "../../../utils/time-utils";


export default function DashboardReportPage() {
    const [showConfigDialog, setShowConfigDialog] = useState(false);
    const [printModeToggled, setPrintModeToggled] = useState(false);
    const [refTime, setRefTime] = useState<Date | undefined>(undefined);
    const toast = useRef<Toast>(null);

    // dashboardId from query params
    const dashboardId = new URLSearchParams(window.location.search).get("dashboardId")!!
    const id = parseInt(dashboardId)

    const query = useGetDashboard(id, {
        onSuccess: (data) => {
            const truncateTo = data?.reportOptions?.timingOptions?.referenceTimeTruncateTo ?? "HOURS"
            const offset = data?.reportOptions?.timingOptions?.referenceTimeOffset ?? 0
            const offsetUnit = data?.reportOptions?.timingOptions?.referenceTimeOffsetUnit ?? "HOURS"
            let refTime = truncateDate(new Date(), truncateTo)
            refTime = addToDate(refTime, offset, offsetUnit)
            setRefTime(refTime)
    }});

    const updateQuery = useUpdateDashboard({
        onSuccess: () => {toast.current?.show({severity: 'success', summary: 'Success', detail: 'Dashboard updated', life: 3000}); setShowConfigDialog(false)},
        onFailure: (error: Error) => {toast.current?.show({severity: 'error', summary: 'Error', detail: error.message, life: 3000})},
    });

    const openPreviewTab = () => {
        window.open(DashboardReportsApi.getPreviewUrl(id, refTime!!), '_blank')?.focus()
    }

    const genAndDlPdf = () => {
        window.open(DashboardReportsApi.getGenerationAndDownloadUrl({dashboardId: id, referenceTime: refTime!!.toISOString()}), '_blank')?.focus()
    }

    const updateDashboard = (dashboard: Dashboard) => {
        updateQuery.mutate(dashboard);
    }


    const leftContents = (
        <React.Fragment>
            <Button label="Edit" icon="pi pi-pencil" className="mr-2" onClick={() => setShowConfigDialog(true)} />
            <Button label="Toggle Print Mode" icon="pi pi-print" className="mr-2 p-button-warning" onClick={() => setPrintModeToggled(!printModeToggled)} />
        </React.Fragment>
    );

    const rightContents = (
        <React.Fragment>
            <Calendar placeholder="Reference time" dateFormat="yy-mm-dd" className="mr-2" value={refTime} showTime hourFormat="24" onChange={(e) => setRefTime(e.value as Date)}/>
            <Button label="Generate & Download PDF" icon="pi pi-file-pdf" className="p-button-help p-mr-2" onClick={genAndDlPdf}/>
            <Button label="Preview" icon="pi pi-file" className="p-button-help" onClick={openPreviewTab}/>
        </React.Fragment>
    );

    const renderBody = () => {
        return (
            <React.Fragment>
                <h1>Dashboard {query.data?.title}</h1>

                { query.data?.dashboardTabsJs?.map((c, i) => {
                    return <div key={i} style={{border: "dotted 2px", padding: "0.5rem", marginTop: "2rem"}}>
                        <h3>Tab {i}: {c.title}</h3>

                        { c.componentGroups?.map((g, j) => {
                            return <div key={j} style={{border: "dashed 2px", padding: "1rem", marginTop: "1rem"}}>
                                <h4>Group {j}</h4>

                                { g.components?.map((component, k) => {
                                    return <div key={k} style={{border: "solid 1px", marginTop: "2.5rem", background: "white"}}>
                                        <h5>Component {i}:  {component.title} [{component.type}]</h5>

                                        <ErrorBoundary>
                                            <DashboardComponentMappingWrapper component={component.type} config={component.config} referenceTime={refTime!!.toISOString()}/>
                                        </ErrorBoundary>
                                    </div>
                                })}
                            </div>
                        })}
                    </div>
                })}
            </React.Fragment>
        )
    }


    return (
        <div className={printModeToggled ? "print-mode-enabled" : ""}>
            <Toast ref={toast}/>
            <Toolbar left={leftContents} right={rightContents} />

            { !query.data && "Loading dashboard config..."}
            { query.data && renderBody() }

            <Dialog header="Edit Dashboard" visible={showConfigDialog}  contentStyle={{overflow: "initial"}} style={{ width: 'min(100%, 1000px)', height: "70vh"}} onHide={() => setShowConfigDialog(false)}>
                <DashboardReportEditor dashboard={query.data!!} onChange={updateDashboard} cancel={() => setShowConfigDialog(false)}/>
            </Dialog>
        </div>
    )
}